import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
    Checkbox, 
    SearchSelect, 
    Toggle, 
    SaveButton, 
    DateInput, 
    NumberInput, 
    Text, 
    Subheader, 
    Contact, 
    formatDate
} from './FieldComponents';
import { AlertCircle, ArrowDown, ArrowUp, CheckCircle2, ChevronDown, CreditCard, Receipt as Dollar, Filter, Loader2, PlusCircle, RefreshCcw, Trash2 } from 'lucide-react';
import '../styles/AccountingPortal.css';
import Modal from './Modal';
import Alert from './Alert';
import { getContactInitialsClassName } from '../utils/colorUtils';

const QUICKBOOKS_MATCH_LABELS = {
    customer: 'Customer',
    vendor: 'Vendor',
    trust_account: 'Trust',
};

const QuickBooksMatchStatus = ({ result, loading, error }) => {
    if (loading && !result) {
        return <span className="qb-match-message"><Loader2 size={12} className="animate-spin" /> Checking</span>;
    }
    if (error && !result) {
        return <span className="qb-match-message error" title={error}><AlertCircle size={12} /> Unavailable</span>;
    }
    if (!result) {
        return <span className="qb-match-message">Not checked</span>;
    }

    const items = result.matches || {};
    const visibleKeys = ['customer'];
    if (result.payee_type === 'Vendor' || items.vendor?.required) visibleKeys.push('vendor');
    if (items.trust_account?.required) visibleKeys.push('trust_account');

    return (
        <div className="qb-match-list">
            {visibleKeys.map((key) => {
                const item = items[key];
                if (!item) return null;
                const title = item.found
                    ? `${QUICKBOOKS_MATCH_LABELS[key]} matched: ${item.name || item.expected}`
                    : `${QUICKBOOKS_MATCH_LABELS[key]} not found: ${item.expected || 'No expected name'}`;
                return (
                    <span
                        key={key}
                        className={`qb-match-item ${item.found ? 'matched' : item.required ? 'missing' : 'optional-missing'}`}
                        title={title}
                    >
                        {item.found ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}
                        {QUICKBOOKS_MATCH_LABELS[key]}
                    </span>
                );
            })}
        </div>
    );
};

export const InvoiceStaging = ({ user, qbConnected, fetchCounts }) => {
    const [invoices, setInvoices] = useState([]);
    const [selectedInvoices, setSelectedInvoices] = useState([]);
    const [filter, setFilter] = useState('');
    const [filterStatus, setFilterStatus] = useState(0);
    const [creditAccount, setCreditAccount] = useState('');
    const [creditAccounts, setCreditAccounts] = useState([]);
    const [debitAccounts, setDebitAccounts] = useState([]);
    const [debitAccount, setDebitAccount] = useState({});
    const [combinedChecks, setCombinedChecks] = useState(true);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [sortField, setSortField] = useState('date_added');
    const [sortDirection, setSortDirection] = useState('desc');
    const [accountOptions, setAccountOptions] = useState([]);
    const [createInvoice, setCreateInvoice] = useState(false);
    const [message, setMessage] = useState(null);
    const [lastSelectedInvoiceId, setLastSelectedInvoiceId] = useState(null);
    const [newInvoice, setNewInvoice] = useState({
        client_name: null,
        case_id: null,
        memo: '',
        payment_type: 'check',
        amount: null,
        date: new Date().toISOString().split('T')[0],
        requestor: user?.id || 12,
        payee: null,
        debit_account: null
    });
    const [cases, setCases] = useState([]);
    const [dataChanged, setDataChanged] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [quickBooksMatches, setQuickBooksMatches] = useState({});
    const [quickBooksMatchLoading, setQuickBooksMatchLoading] = useState(false);
    const [quickBooksMatchError, setQuickBooksMatchError] = useState('');
    const [quickBooksMatchRequestVersion, setQuickBooksMatchRequestVersion] = useState(0);
    const [quickBooksCustomerSelections, setQuickBooksCustomerSelections] = useState({});
    const forceQuickBooksMatchRefresh = useRef(false);
    const searchFetchInitialized = useRef(false);
    const debitAccountRef = useRef(debitAccount);
    const skipNextInvoiceEffect = useRef(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 100,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
    });
    const [perPageOptions] = useState([25, 50, 100, 200, 500]);

    const fetchInvoices = useCallback(async (page = 1, perPage = pagination.per_page, searchTerm = '') => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                per_page: perPage.toString(),
                time: Date.now().toString()
            });
            
            // Add status filter if it's not null (null means "All")
            if (filterStatus !== null) {
                params.append('status', filterStatus.toString());
            }
            
            // Add search filter if present
            if (searchTerm) {
                params.append('search', searchTerm);
            }
            
            const response = await fetch(`https://api.casedb.co/invoices.php?${params}`, { credentials: 'include' });
            if (!response.ok) throw new Error('Network response was not ok');
            
            const data = await response.json();
            if (data.success) {
                setInvoices(data.invoices || []);
                setPagination({
                    current_page: data.pagination?.current_page || page,
                    per_page: data.pagination?.per_page || perPage,
                    total: data.pagination?.total || 0,
                    total_pages: data.pagination?.total_pages || 0,
                    has_next: data.pagination?.has_next || false,
                    has_prev: data.pagination?.has_prev || false
                });
                setDebitAccount({});
            } else {
                console.error("Error loading invoices:", data.message);
            }
        } catch (error) {
            console.error("Error fetching invoices:", error);
        } finally {
            setLoading(false);
        }
    }, [pagination.per_page, filterStatus]);

    const fetchAccountOptions = useCallback(async () => {
        try {
            const response = await fetch(`https://api.casedb.co/accounting.php?action=accounts&time=${Date.now()}`, {
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Network response was not ok');
            
            const data = await response.json();
            if (data.success) {
                const sortedAccounts = (data.accounts || [])
                    .filter(acc => acc?.label)
                    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

                console.log("Sorted Accounts:", sortedAccounts);

                setCreditAccounts(Object.fromEntries(sortedAccounts.map(acc => [acc.value, acc.label])));
                setDebitAccounts(Object.fromEntries(data.accounts.filter(acc => acc.type === 'debit').map(acc => [acc.value, acc.label])));
            } else {
                console.error("Error loading accounts:", data.message);
            }
        } catch (error) {
            console.error("Error fetching account options:", error);
        }
    }, []);

    // Background QuickBooks token refresh
    const refreshQuickBooksConnection = useCallback(async () => {
        if (!qbConnected) return;
        
        try {
            const response = await fetch(`https://api.casedb.co/accounting.php?action=qb_status&time=${Date.now()}`, {
                credentials: 'include'
            });
            const data = await response.json();
            
            if (!data.success || !data.connected) {
                console.log('QuickBooks connection lost, attempting to refresh...');
                // Could trigger a reconnection flow here if needed
                // For now, just log it and let the user know
            }
        } catch (error) {
            console.error('Error checking QuickBooks status:', error);
        }
    }, [qbConnected]);

    // Check QuickBooks connection every 10 minutes
    useEffect(() => {
        if (!qbConnected) return;
        
        const interval = setInterval(refreshQuickBooksConnection, 10 * 60 * 1000); // 10 minutes
        return () => clearInterval(interval);
    }, [qbConnected, refreshQuickBooksConnection]);

    const handlePageChange = (newPage) => {
        fetchInvoices(newPage, pagination.per_page, filter);
    };

    const handlePerPageChange = (newPerPage) => {
        skipNextInvoiceEffect.current = true;
        setPagination(prev => ({ ...prev, current_page: 1, per_page: newPerPage }));
        fetchInvoices(1, newPerPage, filter);
    };

    useEffect(() => {
        if (skipNextInvoiceEffect.current) {
            skipNextInvoiceEffect.current = false;
            return;
        }
        fetchInvoices();
    }, [fetchInvoices]);

    useEffect(() => {
        fetchAccountOptions();
    }, [fetchAccountOptions]);

    useEffect(() => {
        debitAccountRef.current = debitAccount;
    }, [debitAccount]);

    useEffect(() => {
        if (!qbConnected || invoices.length === 0) {
            setQuickBooksMatches({});
            setQuickBooksMatchLoading(false);
            setQuickBooksMatchError('');
            return undefined;
        }

        const refreshQuickBooksData = forceQuickBooksMatchRefresh.current;
        forceQuickBooksMatchRefresh.current = false;
        const controller = new AbortController();
        const timeoutId = setTimeout(async () => {
            setQuickBooksMatchLoading(true);
            setQuickBooksMatchError('');
            try {
                const debitAccountsForMatch = Object.fromEntries(invoices.map((invoice) => {
                    const lastName = invoice.last_name ?? '';
                    const firstName = invoice.first_name ? `, ${invoice.first_name}` : '';
                    const defaultLabel = Number(invoice.type) === 0
                        ? 'Advance Client Costs'
                        : `Trust Liability:${invoice.case_id} ${Number(invoice.is_company) === 1 ? invoice.company_name : (lastName && firstName ? `${lastName}${firstName}` : '')}`;
                    return [invoice.id, debitAccountRef.current[invoice.id] || defaultLabel];
                }));
                const response = await fetch('https://api.casedb.co/accounting.php?action=qb_match', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    signal: controller.signal,
                    body: JSON.stringify({
                        invoice_ids: invoices.map((invoice) => invoice.id),
                        debit_account: debitAccountsForMatch,
                        refresh: refreshQuickBooksData,
                    }),
                });
                const data = await response.json();
                if (!response.ok || !data.success) {
                    throw new Error(data.message || 'Unable to check QuickBooks matches.');
                }
                setQuickBooksMatches(data.matches || {});
            } catch (error) {
                if (error.name !== 'AbortError') {
                    setQuickBooksMatchError(error.message || 'Unable to check QuickBooks matches.');
                }
            } finally {
                if (!controller.signal.aborted) {
                    setQuickBooksMatchLoading(false);
                }
            }
        }, 500);

        return () => {
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [invoices, qbConnected, quickBooksMatchRequestVersion]);

    const handleQuickBooksMatchRefresh = (event) => {
        event.stopPropagation();
        if (quickBooksMatchLoading) return;
        forceQuickBooksMatchRefresh.current = true;
        setQuickBooksMatchRequestVersion((version) => version + 1);
    };

    const loadQuickBooksCustomerOptions = useCallback(async (query) => {
        const response = await fetch(
            `https://api.casedb.co/accounting.php?action=qb_customer_search&q=${encodeURIComponent(query)}`,
            { credentials: 'include' }
        );
        const data = await response.json();
        if (!response.ok || !data.success) {
            throw new Error(data.message || 'Unable to search QuickBooks customers.');
        }
        return data.options || [];
    }, []);

    const getQuickBooksCustomerSelection = useCallback((invoiceId) => {
        if (Object.prototype.hasOwnProperty.call(quickBooksCustomerSelections, invoiceId)) {
            return quickBooksCustomerSelections[invoiceId];
        }
        const matchedCustomer = quickBooksMatches[invoiceId]?.matches?.customer;
        if (!matchedCustomer?.found || !matchedCustomer.id) {
            return null;
        }
        return {
            value: String(matchedCustomer.id),
            label: matchedCustomer.name || matchedCustomer.expected,
        };
    }, [quickBooksCustomerSelections, quickBooksMatches]);

    // Refetch when search filter changes (with debounce)
    useEffect(() => {
        if (!searchFetchInitialized.current) {
            searchFetchInitialized.current = true;
            return undefined;
        }
        const timeoutId = setTimeout(() => {
            fetchInvoices(1, pagination.per_page, filter);
        }, 300); // 300ms debounce
        
        return () => clearTimeout(timeoutId);
    }, [filter]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortedInvoices = [...invoices].sort((a, b) => {
        let comparison = 0;
        
        switch (sortField) {
            case 'date_added':
                comparison = new Date(a.date_added) - new Date(b.date_added);
                break;
            case 'requestor':
                comparison = (a.requestor || '').localeCompare(b.requestor || '');
                break;
            case 'client_name':
                comparison = (a.client_name || '').localeCompare(b.client_name || '');
                break;
            case 'memo':
                comparison = (a.memo || '').localeCompare(b.memo || '');
                break;
            case 'case':
                comparison = (a.case_id || 0) - (b.case_id || 0);
                break;
            case 'payee':
                comparison = (a.payee || '').localeCompare(b.payee || '');
                break;
            case 'payment_type':
                comparison = (a.type || 0) - (b.type || 0);
                break;
            case 'more_info':
                comparison = (a.more_info || '').localeCompare(b.more_info || '');
                break;
            case 'amount':
                comparison = (a.amount || 0) - (b.amount || 0);
                break;
            default:
                comparison = 0;
        }
        
        return sortDirection === 'asc' ? comparison : -comparison;
    });

    // No longer filter client-side - filtering is done server-side
    const filteredInvoices = sortedInvoices;

    const handleSelectAll = () => {
        if (selectedInvoices.length === filteredInvoices.length) {
            setSelectedInvoices([]);
            setLastSelectedInvoiceId(null);
        } else {
            const allIds = filteredInvoices.map(inv => inv.id);
            setSelectedInvoices(allIds);
            // Set the last invoice in the filtered list as the last selected
            setLastSelectedInvoiceId(allIds.length > 0 ? allIds[allIds.length - 1] : null);
        }
        setDataChanged(true);
    };

    const handleSelectInvoice = (id) => {
        setSelectedInvoices(prev => {
            if (prev.includes(id)) {
                // If deselecting, check if this was the last selected
                const newSelected = prev.filter(invId => invId !== id);
                if (id === lastSelectedInvoiceId && newSelected.length > 0) {
                    // Set the last remaining selected invoice as the new last selected
                    setLastSelectedInvoiceId(newSelected[newSelected.length - 1]);
                } else if (newSelected.length === 0) {
                    // If no invoices left selected, clear the last selected
                    setLastSelectedInvoiceId(null);
                }
                return newSelected;
            } else {
                // If selecting, this becomes the new last selected
                setLastSelectedInvoiceId(id);
                return [...prev, id];
            }
        });
        setDataChanged(true);
    };

    const handleDelete = async () => {
        if (selectedInvoices.length === 0) return;
        
        if (window.confirm(`Are you sure you want to delete ${selectedInvoices.length} selected invoice(s)?`)) {
            try {
                setLoading(true);
                const response = await fetch('https://api.casedb.co/accounting.php?action=delete', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        invoice_ids: selectedInvoices,
                        user_id: user?.user_id
                    }),
                });
                
                const data = await response.json();
                if (data.success) {
                    setInvoices(prev => prev.filter(inv => !selectedInvoices.includes(inv.id)));
                    setSelectedInvoices([]);
                    setLastSelectedInvoiceId(null);
                    setDataChanged(false);
                    fetchCounts?.();
                } else {
                    console.error('Error deleting invoices:', data.message);
                }
            } catch (error) {
                console.error('Error deleting invoices:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleProcess = async () => {
        if (selectedInvoices.length === 0) {
            alert('No invoices selected. Please select at least one invoice to process.');
            return;
        }
        
        if (!creditAccount) {
            alert('Please select a credit account to proceed.');
            return;
        }

        if (!qbConnected) {
            alert('Please connect to QuickBooks first to process invoices.');
            return;
        }
        
        setProcessing(true);
        try {
            setLoading(true);
            
            // Prepare formatted memos and custom field updates for each invoice
            const invoiceData = selectedInvoices.map(invoiceId => {
                const invoice = invoices.find(inv => inv.id === invoiceId);
                const lastName = invoice.last_name ?? '';
                const firstName = invoice.first_name ? `, ${invoice.first_name}` : '';
                const clientName = lastName && firstName ? `${lastName}${firstName}` : `${invoice.company_name || ''}`;
                
                // Format memo as "case_id clientName/memo"
                const formattedMemo = `${invoice.case_id} ${clientName}/${invoice.memo || ''}`.trim();
                
                return {
                    invoice_id: invoiceId,
                    formatted_memo: formattedMemo,
                    case_id: invoice.case_id,
                    invoice_type: Number(invoice.type),
                    group_id: invoice.group_id // Assuming group_id is available in invoice data
                };
            });
            
            // Extract formatted memos for backend
            const formattedMemos = Object.fromEntries(
                invoiceData.map(data => [data.invoice_id, data.formatted_memo])
            );
            
            // Prepare custom field updates
            const customFieldUpdates = Object.fromEntries(
                invoiceData.map(data => [
                    data.invoice_id, 
                    [{
                        field_id: data.invoice_type === 0 ? 282 : 292, // 282 for expenses, 292 for trust accounting
                        value: 1
                    }]
                ])
            );
            
            // Prepare document attachments flag (only for expense invoices, not trust accounting)
            const documentAttachments = Object.fromEntries(
                selectedInvoices.map(id => {
                    const invoice = invoices.find(inv => inv.id === id);
                    // Only enable attachments for expense invoices (type 0), not trust accounting (type 1)
                    return [id, Number(invoice.type) === 0];
                })
            );
            
            const response = await fetch('https://api.casedb.co/accounting.php?action=process', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    invoice_ids: selectedInvoices,
                    credit_account: creditAccount,
                    combine_checks: combinedChecks,
                    combine_deposits: combinedChecks,
                    payment_type: Number(invoices.find(inv => selectedInvoices.includes(inv.id))?.type || 0),
                    debit_account: Object.fromEntries(
                        selectedInvoices.map(id => {
                            const invoice = invoices.find(inv => inv.id === id);
                            const lastName = invoice.last_name ?? '';
                            const firstName = invoice.first_name ? `, ${invoice.first_name}` : '';
                            const defaultLabel = Number(invoice.type) === 0
                                ? 'Advance Client Costs'
                                : `Trust Liability:${invoice.case_id} ${invoice.is_company === 1 ? invoice.company_name : (lastName && firstName ? `${lastName}${firstName}` : '')}`;
                            return [id, debitAccount[id] || defaultLabel];
                        })
                    ),
                    // Send formatted memos in expected format
                    formatted_memos: formattedMemos,
                    // Send custom field updates
                    custom_field_updates: customFieldUpdates,
                    // Send document attachments flag
                    document_attachments: documentAttachments,
                    customer_overrides: Object.fromEntries(
                        selectedInvoices
                            .map((id) => [id, getQuickBooksCustomerSelection(id)])
                            .filter(([, selection]) => selection?.value)
                            .map(([id, selection]) => [
                                id,
                                { id: selection.value, name: selection.label }
                            ])
                    ),
                    user_id: user?.user_id
                }),
            });
        
            const data = await response.json();
            if (data.success) {
                fetchInvoices();
                fetchCounts?.();
                setSelectedInvoices([]);
                setLastSelectedInvoiceId(null);
                setCreditAccount('');
                setDebitAccounts({});
                setQuickBooksCustomerSelections({});
                setDataChanged(false);
                setMessage({ type: 'success', text: 'Invoice(s) processed successfully!' });
            } else {
                console.error('Error processing invoices:', data.message);
                setMessage({ type: 'error', text: data.message || 'Failed to process invoice(s)' });
            }
        } catch (error) {
            console.error('Error processing invoices:', error);
            setMessage({ type: 'error', text: 'An unexpected error occurred. Please try again.' });
        } finally {
            setLoading(false);
            setProcessing(false);
        }
    };

    const handleDebitAccountChange = (invoiceId, accountId) => {
        setDebitAccounts(prev => ({
            ...prev,
            [invoiceId]: accountId
        }));
        setDataChanged(true);
    };

    const handleCreateInvoice = async () => {
        if (!newInvoice.case_id || !newInvoice.amount) {
            alert('Please fill in all required fields (Client Name and Amount)');
            return;
        }
        
        try {
            setLoading(true);
            const response = await fetch('https://api.casedb.co/accounting.php?action=create_invoice', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    ...newInvoice,
                    user_id: user?.user_id ?? 12
                }),
        });
        
        const data = await response.json();
        if (data.success) {
            fetchInvoices();
            fetchCounts?.();
            setCreateInvoice(false);
            setNewInvoice({
                case_id: null,
                client_name: 0,
                memo: '',
                type: null,
                amount: 0,
                requestor: user?.user_id ?? null,
                payee: '',
                debit_account: ''
            });
        } else {
            console.error('Error creating invoice:', data.message);
            alert(`Error: ${data.message || 'Failed to create invoice'}`);
        }
        } catch (error) {
            console.error('Error creating invoice:', error);
            alert('An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchCases = useCallback(async () => {
        const response = await fetch('https://api.casedb.co/invoice-cases.php', { credentials: 'include' });
        const data = await response.json();
        if (data.success) {
            setCases(data.cases || []);
        } else {
            console.error("Error loading cases:", data.message);
        }
    }, []);

    useEffect(() => {
        if (createInvoice && cases.length === 0) {
            fetchCases();
        }
    }, [cases.length, createInvoice, fetchCases]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const totalSelected = selectedInvoices.length > 0 
        ? invoices
            .filter(inv => selectedInvoices.includes(inv.id))
            .reduce((sum, inv) => sum + Number(inv.amount), 0)
        : 0;

    const formatName = (fullName) => {
        if (!fullName) return '';
        
        const nameParts = fullName.trim().split(' ');
        
        if (nameParts.length === 1) {
            return nameParts[0];
        }
        
        const lastName = nameParts.pop();
        const firstName = nameParts.join(' ');
        
        return `${lastName}, ${firstName}`;
    };

    return (
        <div className="invoice-staging">
            <div className="accounting-section">
                <div className="accounting-header">
                    <div className="accounting-header-left">
                        <h3>Invoice Staging {selectedInvoices.length > 0 && `(${selectedInvoices.length} selected)`}</h3>
                        <div className="filter-buttons">
                            <button 
                                className={`filter-button ${filterStatus === 0 ? 'active' : ''}`} 
                                onClick={() => setFilterStatus(0)}
                            >
                                Pending
                            </button>
                            <button 
                                className={`filter-button ${filterStatus === 1 ? 'active' : ''}`} 
                                onClick={() => setFilterStatus(1)}
                            >
                                Processed
                            </button>
                            <button 
                                className={`filter-button ${filterStatus === 2 ? 'active' : ''}`} 
                                onClick={() => setFilterStatus(2)}
                            >
                                Errors
                            </button>
                            <button 
                                className={`filter-button ${filterStatus === null ? 'active' : ''}`} 
                                onClick={() => setFilterStatus(null)}
                            >
                                All
                            </button>
                        </div>
                    </div>
                
                    <div className="accounting-header-right">
                        <div className='filter-group'>
                            <div className="select-wrapper">
                                <select 
                                    className='filter-select' 
                                    value={pagination.per_page} 
                                    onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                                >
                                    {perPageOptions.map(option => (
                                        <option key={option} value={option}>{option} per page</option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>

                        <button className="action alt" onClick={() => setFilterOpen(!filterOpen)}>
                            <Filter size={16} />
                            Filter
                        </button>
                        
                        <button className="action alt" onClick={() => fetchInvoices(pagination.current_page, pagination.per_page)}>
                            <RefreshCcw size={16} />
                            Refresh
                        </button>
                        
                        <button className="action" onClick={() => setCreateInvoice(true)}>
                            <PlusCircle size={16} />
                            New Invoice
                        </button>
                    </div>
                </div>
                
                {filterOpen && (
                    <div className="filter-section">
                        <div className="keystroke-filter">
                            <input 
                                type="text" 
                                style={{ backgroundColor: 'var(--background-color)' }}
                                value={filter} 
                                onChange={(e) => setFilter(e.target.value)} 
                                placeholder="Search by client, case, memo, or payee"
                            />
                        </div>
                        
                        <div className="filter-actions">
                            <button className="action alt" onClick={() => setFilter('')}>Reset</button>
                            <button 
                                className="action alt" 
                                onClick={() => handleSelectAll(!selectedInvoices.length || selectedInvoices.length !== filteredInvoices.length)}
                            >
                                {selectedInvoices.length === filteredInvoices.length ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                    </div>
                )}
                
                <div className="invoice-table-container">
                    <table className="invoice-table subtext">
                        <thead>
                            <tr>
                                <th className='invoice-select'>
                                    <Checkbox 
                                        value={selectedInvoices.length > 0 && selectedInvoices.length === filteredInvoices.length} 
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th className="sortable invoice-small" onClick={() => handleSort('date_added')}>
                                    Date Added {sortField === 'date_added' && (sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />)}
                                </th>
                                <th className="sortable invoice-small" onClick={() => handleSort('requestor')}>
                                    Requestor {sortField === 'requestor' && (sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />)}
                                </th>
                                <th className="sortable invoice-small" onClick={() => handleSort('client_name')}>
                                    Client Name {sortField === 'client_name' && (sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />)}
                                </th>
                                <th className="sortable invoice-medium" onClick={() => handleSort('memo')}>
                                    Memo {sortField === 'memo' && (sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />)}
                                </th>
                                <th className="sortable invoice-small" onClick={() => handleSort('case')}>
                                    Case {sortField === 'case' && (sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />)}
                                </th>
                                <th className="sortable invoice-small" onClick={() => handleSort('payee')}>
                                    Payee {sortField === 'payee' && (sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />)}
                                </th>
                                <th className="sortable invoice-small" onClick={() => handleSort('payment_type')}>
                                    Type {sortField === 'payment_type' && (sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />)}
                                </th>
                                <th className="sortable invoice-small" onClick={() => handleSort('more_info')}>
                                    More Info {sortField === 'more_info' && (sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />)}
                                </th>
                                <th className='invoice-large'>Debit Account</th>
                                <th className="sortable invoice-small" onClick={() => handleSort('amount')}>
                                    Amount {sortField === 'amount' && (sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />)}
                                </th>
                                <th className="invoice-match">
                                    <span className="qb-match-heading">
                                        QB Match
                                        <button
                                            type="button"
                                            className="qb-match-refresh"
                                            onClick={handleQuickBooksMatchRefresh}
                                            disabled={!qbConnected || quickBooksMatchLoading}
                                            title="Check QuickBooks again now"
                                            aria-label="Refresh QuickBooks matches"
                                        >
                                            <RefreshCcw size={12} className={quickBooksMatchLoading ? 'animate-spin' : ''} />
                                        </button>
                                    </span>
                                </th>
                                <th className="invoice-small">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="13" className="loading-row">Loading invoices...</td>
                                </tr>
                            ) : filteredInvoices.length === 0 ? (
                                <tr>
                                    <td colSpan="13" className="empty-row">No invoices found</td>
                                </tr>
                            ) : (
                                (() => {
                                    const rows = [];
                                    // No need to filter by status here since backend handles it
                                    
                                    filteredInvoices.forEach((invoice, index) => {
                                        // Add the regular invoice row
                                        rows.push(
                                            <tr 
                                                key={invoice.id} 
                                                className={`invoice-row ${selectedInvoices.includes(invoice.id) ? 'selected-row' : ''} ${invoice.status === 'processed' ? 'processed-row' : invoice.status === 'error' ? 'error-row' : ''} ${invoice.amount < 0 ? 't-dark' : ''}`}
                                                style={{ backgroundColor: invoice.amount < 0 ? "var(--settlement-color)" : selectedInvoices.includes(invoice.id) ? "var(--border-color)" : "" }}
                                                onClick={() => handleSelectInvoice(invoice.id)}
                                            >
                                                <td className="invoice-select">
                                                    <Checkbox 
                                                        value={selectedInvoices.includes(invoice.id)} 
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleSelectInvoice(invoice.id)
                                                        }}
                                                    />
                                                </td>
                                                <td className='invoice-small'>{formatDate(invoice.date_added)[3]}</td>
                                                <td className='invoice-small'>@{invoice.requestor}</td>
                                                <td className='invoice-small'>{invoice.client_name}</td>
                                                <td className='invoice-medium' title={invoice.memo}>
                                                    {invoice.case_id}/{invoice.last_name && invoice.first_name ? `${invoice.last_name}, ${invoice.first_name}` : `${invoice.company_name || ''}`}/{invoice?.memo}
                                                </td>
                                                <td className='invoice-small' onClick={(e) => e.stopPropagation()}>{<a href={`/case/${invoice.case_id}?section=${Number(invoice.type) === 0 ? '25' : '26'}`} target='_blank' className='subtext-link fill-background-contrast'>{invoice.case_id}</a>}</td>
                                                <td className='invoice-small' title={invoice?.payee}>{invoice?.payee}</td>
                                                <td className='invoice-small'>
                                                    {Number(invoice.type) === 0 ? (
                                                        <span className="pt expenses">Expenses</span>
                                                    ) : (
                                                        <span className="pt trust">Trust Accounting</span>
                                                    )}
                                                </td>
                                                <td className='invoice-small' title={invoice?.more_info}>{invoice?.more_info?.length > 15 ? `${invoice?.more_info.slice(0, 15)}...` : invoice?.more_info}</td>
                                                <td className='form-group nm invoice-large' onClick={(e) => e.stopPropagation()}>
                                                    <input 
                                                        type="text"
                                                        value={debitAccount.hasOwnProperty(invoice.id)
                                                            ? debitAccount[invoice.id]
                                                            : Number(invoice.type) === 0
                                                            ? 'Advance Client Costs'
                                                            : `Trust Liability:${invoice.case_id} ${Number(invoice.is_company) === 1 ? invoice.company_name : (invoice.last_name && invoice.first_name ? `${invoice.last_name}, ${invoice.first_name}` : '')}`
                                                        }
                                                        onChange={(e) => setDebitAccount({ ...debitAccount, [invoice.id]: e.target.value })}
                                                        placeholder="Debit Account"
                                                        style={{ fontSize: '12.8px' }}
                                                    />
                                                </td>
                                                <td className='invoice-small'>{formatCurrency(invoice.amount)}</td>
                                                <td className="invoice-match">
                                                    <QuickBooksMatchStatus
                                                        result={quickBooksMatches[invoice.id]}
                                                        loading={quickBooksMatchLoading}
                                                        error={quickBooksMatchError}
                                                    />
                                                </td>
                                                <td className='invoice-small'>
                                                    {invoice.status === '1' ? (
                                                        <span className="status-indicator processed">Processed</span>
                                                    ) : invoice.status === '2' ? (
                                                        <span className="status-indicator error" title={invoice?.error}>Error</span>
                                                    ) : (
                                                        <span className="status-indicator pending">Pending</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                        
                                        // Add the credit account popup row after the last selected invoice
                                        if (selectedInvoices.length > 0 && invoice.id === lastSelectedInvoiceId) {
                                            rows.push(
                                                <tr key="credit-account-popup" className="credit-account-popup-row">
                                                    <td colSpan="13" className="credit-account-popup-cell">
                                                        <div className="processing-options-inline">
                                                            <div className="account-selection">
                                                                <div className="form-group nm" style={{ marginBottom: '10px', minWidth: '200px', maxWidth: '300px' }}>
                                                                    <label>Credit Account:</label>
                                                                    <SearchSelect
                                                                        value={creditAccount}
                                                                        onChange={setCreditAccount}
                                                                        options={creditAccounts}
                                                                        placeholder="Select credit account"
                                                                    />
                                                                </div>
                                                                <div className="qb-customer-assignments">
                                                                    <label>QuickBooks Customer:</label>
                                                                    {selectedInvoices.map((selectedInvoiceId) => {
                                                                        const selectedInvoice = invoices.find((item) => item.id === selectedInvoiceId);
                                                                        if (!selectedInvoice) return null;
                                                                        const selection = getQuickBooksCustomerSelection(selectedInvoiceId);
                                                                        const clientName = selectedInvoice.client_name
                                                                            || selectedInvoice.company_name
                                                                            || `Case ${selectedInvoice.case_id}`;
                                                                        const initials = clientName
                                                                            .split(/\s+/)
                                                                            .filter(Boolean)
                                                                            .slice(0, 2)
                                                                            .map((part) => part[0]?.toUpperCase())
                                                                            .join('');
                                                                        return (
                                                                            <div className="qb-customer-assignment" key={selectedInvoiceId}>
                                                                                <div className="qb-customer-contact">
                                                                                    <span
                                                                                        className="qb-customer-initials"
                                                                                        style={{
                                                                                            ...getContactInitialsClassName(
                                                                                                selectedInvoice.client_contact_id || selectedInvoice.case_id,
                                                                                                { color: true }
                                                                                            ),
                                                                                            color: '#1d2125',
                                                                                        }}
                                                                                    >
                                                                                        {initials || 'C'}
                                                                                    </span>
                                                                                    <span className="qb-customer-context">
                                                                                        <strong>{formatName(clientName)}</strong>
                                                                                        <small>Case {selectedInvoice.case_id}</small>
                                                                                    </span>
                                                                                </div>
                                                                                <div className="qb-customer-select">
                                                                                    <SearchSelect
                                                                                        value={selection?.value || null}
                                                                                        onChange={(option) => setQuickBooksCustomerSelections((current) => {
                                                                                            const next = { ...current };
                                                                                            if (option) {
                                                                                                next[selectedInvoiceId] = option;
                                                                                            } else {
                                                                                                delete next[selectedInvoiceId];
                                                                                            }
                                                                                            return next;
                                                                                        })}
                                                                                        options={selection ? [selection] : []}
                                                                                        loadOptions={loadQuickBooksCustomerOptions}
                                                                                        loadOptionsMinChars={2}
                                                                                        loadOptionsDebounceMs={400}
                                                                                        returnOptionObject
                                                                                        portal
                                                                                        placeholder="Search QuickBooks customers"
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                                <div className="toggle-options">
                                                                    <Toggle
                                                                        value={combinedChecks}
                                                                        onChange={setCombinedChecks}
                                                                        label={<span>Checks: Write one check per payee<br />Deposits: Combine deposits</span>}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="processing-actions">
                                                                <button 
                                                                    className="action destroy" 
                                                                    onClick={handleDelete} 
                                                                    disabled={selectedInvoices.length === 0}
                                                                >
                                                                    <Trash2 size={16} />
                                                                    Delete
                                                                </button>
                                                                
                                                                <button 
                                                                    className="action" 
                                                                    onClick={handleProcess}
                                                                    disabled={selectedInvoices.length === 0 || !creditAccount || !qbConnected || processing}
                                                                >
                                                                    {processing && <Loader2 className="animate-spin" />} Process to QuickBooks
                                                                </button>
                                                            </div>
                                                            <div className="total-bar">
                                                                <div className="total-amount">
                                                                    Total: {formatCurrency(totalSelected)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    });
                                    
                                    return rows;
                                })()
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div className='pagination-controls' style={{ justifyContent: 'center', marginTop: '20px' }}>
                    <button 
                        className='action alt' 
                        disabled={!pagination.has_prev}
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                    >
                        Previous
                    </button>
                    <span className='pagination-info subtext'>
                        Page {pagination.current_page} of {pagination.total_pages} ({pagination.total} total invoices)
                    </span>
                    <button 
                        className='action alt' 
                        disabled={!pagination.has_next}
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>
        
            {createInvoice && (
                <Modal
                    onClose={() => setCreateInvoice(false)}
                    title="Create Invoice"
                    header={
                        <div className='modal-header-actions'>
                            <button className="action alt" onClick={() => setCreateInvoice(false)}>Cancel</button>
                            <button className="action" onClick={handleCreateInvoice}>Create Invoice</button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper'>
                        <div className="form-row">
                            <div className="form-group full">
                                <label>Case</label>
                                <SearchSelect
                                    value={newInvoice.case_id}
                                    onChange={(val) => setNewInvoice({...newInvoice, case_id: val})}
                                    options={Object.fromEntries(cases.sort((a, b) => Number(b.id) - Number(a.id)).map(c => [c.id, c.case_name]))}
                                    placeholder="Select case"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Payee</label>
                                <Contact
                                    selectedContact={newInvoice.payee}
                                    setSelectedContact={(contact) => setNewInvoice({...newInvoice, payee: contact?.id || contact})}
                                    onCreateNewContact={() => {}}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Memo</label>
                                <Text 
                                    type="text"
                                    placeholder="Enter memo"
                                    value={newInvoice.memo}
                                    onChange={(val) => setNewInvoice({...newInvoice, memo: val})}
                                />
                            </div>
                            <div className="form-group">
                                <label>Payment Type</label>
                                <div className="payment-type-selector">
                                    <select className='default-select' value={newInvoice.payment_type} onChange={(e) => setNewInvoice({...newInvoice, payment_type: e.target.value})}>
                                        <option value="expense">Expense</option>
                                        <option value="trust-accounting">Trust Accounting</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Amount *</label>
                                <NumberInput 
                                    type="currency" 
                                    value={newInvoice.amount}
                                    onChange={(val) => setNewInvoice({...newInvoice, amount: val})}
                                />
                            </div>
                            <div className="form-group">
                                <label>Date</label>
                                <DateInput 
                                    value={newInvoice.date}
                                    onChange={(val) => setNewInvoice({...newInvoice, date: val})}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Debit Account</label>
                                <SearchSelect
                                    value={newInvoice.debit_account}
                                    onChange={(val) => setNewInvoice({...newInvoice, debit_account: val})}
                                    options={debitAccounts}
                                    placeholder="Select account"
                                />
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
            {message && <Alert type={message.type} message={message.text} onClose={() => setMessage(null)} />}
        </div>
    );
};

export default InvoiceStaging;
